import React, { useContext, useEffect, useState } from 'react'
import { get } from '~/utils/api'
import { ChatContext } from './chatConn'
import { IframeContext } from './IframeConn'
import { WebsocketContext } from './WebsocketConn'

type State = {
  activePhone: boolean
  activeChat: boolean
  isLoadingPhone: boolean
  isLoadingChat: boolean
  pauseEnable: (service: PauseService) => Promise<void>
  pauseDisable: (service: PauseService) => Promise<void>
  setInitialChatPause: () => void
  activeChatSupervisor: boolean
}

export const PauseContext = React.createContext<State>({
  activePhone: false,
  activeChat: false,
  isLoadingPhone: false,
  isLoadingChat: false,
  pauseEnable: async () => {},
  pauseDisable: async () => {},
  setInitialChatPause: () => {},
  activeChatSupervisor: false,
})

type PauseProps = {
  phonePauseEnabled: boolean
  children: React.ReactNode
}

export type PauseService = 'phone' | 'chat'

type WSPayload = { service: PauseService }

const Pause: React.FC<PauseProps> = ({ children, phonePauseEnabled }) => {
  const [activePhone, setActivePhone] = useState(phonePauseEnabled)
  const [activeChat, setActiveChat] = useState(false)
  const [isLoadingChat, setLoadingChat] = useState(false)
  const [isLoadingPhone, setLoadingPhone] = useState(false)
  const iframeContext = useContext(IframeContext)
  const [activeChatSupervisor, setActiveChatSupervisor] = useState(false)

  const setInitialChatPause = () => {
    setActiveChat(true)
  }

  const { userChannel } = useContext(WebsocketContext)
  const {
    isConnected,
    channel: chatChannel,
    pauseState: initialPauseFromChat,
    setPause: setPauseChat,
    activePauseSupervisor,
  } = useContext(ChatContext)

  useEffect(() => {
    setActiveChatSupervisor(activePauseSupervisor)
  }, [activePauseSupervisor])

  useEffect(() => {
    phonePauseEnabled
      ? iframeContext.parentRef.current?.pauseEnabled('phone')
      : iframeContext.parentRef.current?.pauseDisabled('phone')
  }, [])

  const pauseEnable = async (service: PauseService) => {
    const broadcast = () => {
      userChannel?.push('broadcast', {
        event: 'pause_enabled',
        payload: { service },
      })
    }

    if (service == 'phone') {
      if (window.c.phoneEnabled) {
        setLoadingPhone(true)
        await get('agent/snooze')
        broadcast()
      }
    } else {
      if (window.c.chatEnabled) {
        setLoadingChat(true)
        setPauseChat(true)
        broadcast()
      }
    }
  }

  const pauseDisable = async (service: PauseService) => {
    const broadcast = () => {
      userChannel?.push('broadcast', {
        event: 'pause_disabled',
        payload: { service },
      })
    }

    if (service == 'phone') {
      if (window.c.phoneEnabled) {
        setLoadingPhone(true)
        await get('agent/wake')
        broadcast()
      }
    } else {
      if (window.c.chatEnabled) {
        setLoadingChat(true)
        setPauseChat(false)
        broadcast()
      }
    }
  }

  iframeContext.pauseEnableRef.current = pauseEnable
  iframeContext.pauseDisableRef.current = pauseDisable

  useEffect(() => {
    if (!isConnected) {
      return
    }

    if (initialPauseFromChat) {
      setActiveChat(true)
      iframeContext.parentRef.current?.pauseEnabled('chat')
    }

    chatChannel.on('pause', ({ state }: { state: boolean }) => {
      setLoadingChat(false)

      setActiveChat(state)
    })
  }, [isConnected])

  useEffect(() => {
    if (!userChannel) {
      return
    }

    userChannel.on('pause_enabled', (props: WSPayload) => {
      if (props.service == 'phone') {
        setActivePhone(true)
      } else {
        setActiveChat(true)
      }

      setLoadingPhone(false)

      iframeContext.parentRef.current?.pauseEnabled(props.service)
    })

    userChannel.on('pause_disabled', (props: WSPayload) => {
      if (props.service == 'phone') {
        setActivePhone(false)
      } else {
        setActiveChat(false)
      }

      setLoadingPhone(false)

      iframeContext.parentRef.current?.pauseDisabled(props.service)
    })
  }, [!!userChannel])

  return (
    <PauseContext.Provider
      value={{
        activePhone,
        activeChat,
        isLoadingChat,
        isLoadingPhone,
        pauseEnable,
        pauseDisable,
        setInitialChatPause,
        activeChatSupervisor,
      }}
    >
      {children}
    </PauseContext.Provider>
  )
}

export default Pause
